import { supabase } from "@/shared/lib/supabase";

type NotificationEvent =
  | "task_submitted"
  | "asset_search_started"
  | "signup"
  | "professional_signup"
  | "account_deletion"
  | "document_upload"
  | "inventory_complete"
  | "invited_member_joined"
  | "get_authorized_started"
  | "ein_ssn_submitted";

async function sendSlackNotification(
  event: NotificationEvent,
  data: Record<string, unknown> = {},
): Promise<boolean> {
  const { data: response, error } = await supabase.functions.invoke("communications/slack-notify", {
    body: { event, data },
  });
  if (error) throw error;
  return !(response as Record<string, unknown> | null)?.skipped;
}

export async function notifyTaskSubmitted(data: {
  taskName: string;
  taskId: string;
  estateId: number;
  extra?: Record<string, unknown>;
}): Promise<void> {
  await sendSlackNotification("task_submitted", {
    task_name: data.taskName,
    task_id: data.taskId,
    estate_id: data.estateId,
    ...data.extra,
  });
}

export async function notifyAssetSearchStarted(estateId: number): Promise<void> {
  await sendSlackNotification("asset_search_started", { estate_id: estateId });
}

export async function notifySignup(): Promise<void> {
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  await sendSlackNotification("signup", { device_type: isMobile ? "mobile" : "desktop" });
}

export async function notifyAccountDeletion(): Promise<void> {
  await sendSlackNotification("account_deletion");
}

export async function notifyInvitedMemberJoined(
  estateId: number,
  relationship: string,
): Promise<void> {
  await sendSlackNotification("invited_member_joined", {
    estate_id: estateId,
    relationship,
  });
}

export async function notifyEinSsnSubmitted(estateId: number): Promise<void> {
  await sendSlackNotification("ein_ssn_submitted", { estate_id: estateId });
}

export async function notifyGetAuthorizedStarted(data: {
  estateId: number;
  authorityPath: string | null;
  county: string | null;
}): Promise<boolean> {
  return sendSlackNotification("get_authorized_started", {
    estate_id: data.estateId,
    authority_path: data.authorityPath,
    county: data.county,
  });
}

export async function notifyProfessionalSignup(): Promise<void> {
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  await sendSlackNotification("professional_signup", {
    device_type: isMobile ? "mobile" : "desktop",
  });
}

export async function notifyBookingFlowOpened(): Promise<void> {
  const { error } = await supabase.functions.invoke("communications/booking-flow-opened", {
    body: {},
  });
  if (error) {
    console.error("Failed to notify booking flow opened:", error);
  }
}
